import React from 'react';
import moment from 'moment';
import i18next from 'i18next';
import { Layout } from '@thinknet-gmbh/platform-common-ui';
import { Routes, Route } from 'react-router-dom';
import { useGoogleFonts } from '@flayyer/use-googlefonts';
import { ThemeProvider } from '@thinknet-gmbh/platform-common-ui';
import { PERMISSION_ACTIONS } from '@thinknet-gmbh/platform-common';

import { ApolloProvider } from 'src/providers';
import { useSessionContext } from 'src/contexts';
import LayoutWrapper from 'src/components/app/layout-wrapper/layout-wrapper.component';
import SubscriptionProvider from 'src/providers/subscription.provider';
import CompanyContextProvider from 'src/providers/company.provider';
import PermissionContextProvider from 'src/providers/permission.provider';

import { ROUTES, ROUTES_RELATIONS } from './app-routes.constants';
import { ProtectedRoute, ProtectedRouteProps } from './protected-route';

moment.updateLocale(i18next.language, { week: { dow: 1 } });

export const App: React.FC = () => {
  const [sessionContext, updateSessionContext] = useSessionContext();

  const setRedirectPath = (path: string) => {
    updateSessionContext({ ...sessionContext, redirectPath: path });
  };

  const defaultProtectedRouteProps: ProtectedRouteProps = {
    isAuthenticated: sessionContext.isAuthenticated,
    authenticationPath: ROUTES.auth,
    redirectPath: sessionContext.redirectPath,
    setRedirectPath: setRedirectPath,
    action: PERMISSION_ACTIONS.read,
  };

  useGoogleFonts([{ family: 'Inter', styles: ['400..700'] }]);

  return (
    <ApolloProvider>
      <PermissionContextProvider>
        <CompanyContextProvider>
          <ThemeProvider>
            <Routes>
              {ROUTES_RELATIONS.map(
                ({ path, PageComponent, isProtected = true, children, permission }) => (
                  <Route
                    key={path}
                    path={path}
                    element={
                      isProtected ? (
                        <ProtectedRoute {...defaultProtectedRouteProps} permission={permission}>
                          <LayoutWrapper isProtected={isProtected}>
                            <SubscriptionProvider>
                              <PageComponent />
                            </SubscriptionProvider>
                          </LayoutWrapper>
                        </ProtectedRoute>
                      ) : (
                        <PageComponent />
                      )
                    }
                  >
                    {children?.map(({ subpath, SubPageComponent, withoutSidebar }: any) => {
                      const LayoutComponent = withoutSidebar ? Layout : LayoutWrapper;
                      return (
                        <Route
                          key={subpath}
                          path={subpath}
                          element={
                            <LayoutComponent>
                              <SubPageComponent />
                            </LayoutComponent>
                          }
                        />
                      );
                    })}
                  </Route>
                )
              )}
            </Routes>
          </ThemeProvider>
        </CompanyContextProvider>
      </PermissionContextProvider>
    </ApolloProvider>
  );
};

export default App;
